/**
 * Booking Management block: self-service booking management interface.
 *
 * Fetches booking details by token from the backend API. Displays the
 * booking date, time, meeting type, location, and contact name. Provides
 * "Reschedule" and "Cancel" action buttons.
 */

import { registerBlockRenderer, getBlockRendererContext } from './registry';
import { escapeHtml, createBlockWrapper, createErrorState } from './utils';
import type { BookingManagementData } from './types';
import { inlineLoaderHTML } from '../../core/loading/activity-loader';

// =============================================================================
// BOOKING DETAILS RESPONSE SHAPE (from backend)
// =============================================================================

interface BookingDetails {
  id?: string;
  token: string;
  status?: string;                    // e.g., "confirmed", "cancelled", "pending"
  startTime?: string;                 // ISO 8601 datetime
  endTime?: string;                   // ISO 8601 datetime
  timezone?: string;                  // IANA timezone
  meetingType?: string;               // Meeting type name
  location?: string;                  // Location name
  contactName?: string;               // Booked-by name
  contactEmail?: string;              // Booked-by email
  notes?: string;                     // Booking notes
}

// =============================================================================
// BOOKING MANAGEMENT BLOCK
// =============================================================================

registerBlockRenderer('booking-management', (data: unknown, container: HTMLElement, onAction?: (value: string) => void) => {
  const bmData = data as BookingManagementData;
  const ctx = getBlockRendererContext();

  const wrapper = createBlockWrapper('booking-management');

  // Title
  const title = document.createElement('div');
  title.className = 'pms-booking-mgmt-title';
  title.textContent = 'Your Booking';
  wrapper.appendChild(title);

  // Content area — starts with loading state
  const contentArea = document.createElement('div');
  contentArea.className = 'pms-booking-mgmt-content';
  contentArea.innerHTML = `<div class="pms-booking-mgmt-loading">${inlineLoaderHTML('Loading booking details...')}</div>`;
  wrapper.appendChild(contentArea);

  container.appendChild(wrapper);

  // =========================================================================
  // FETCH & RENDER
  // =========================================================================

  async function fetchBooking(): Promise<void> {
    const workspaceId = bmData.workspaceId || ctx.workspaceId;

    if (!ctx.apiClient || !workspaceId) {
      contentArea.innerHTML = createErrorState('Unable to load booking details');
      return;
    }

    try {
      const baseUrl = ctx.apiClient.getBaseUrl();
      const url = `${baseUrl}/api/calendar/bookings/${encodeURIComponent(bmData.token)}`;

      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) {
        if (response.status === 404) {
          contentArea.innerHTML = createErrorState('Booking not found. The link may have expired or the booking no longer exists.');
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const booking: BookingDetails = await response.json();
      renderBookingDetails(booking);
    } catch (_err) {
      contentArea.innerHTML = createErrorState('Unable to load booking details right now. Please try again later.');
    }
  }

  function renderBookingDetails(booking: BookingDetails): void {
    contentArea.innerHTML = '';

    // Status badge
    if (booking.status) {
      const statusBadge = document.createElement('div');
      const statusClass = booking.status === 'confirmed' ? 'pms-booking-mgmt-status-confirmed'
        : booking.status === 'cancelled' ? 'pms-booking-mgmt-status-cancelled'
        : 'pms-booking-mgmt-status-pending';
      statusBadge.className = `pms-booking-mgmt-status ${statusClass}`;
      statusBadge.textContent = booking.status.charAt(0).toUpperCase() + booking.status.slice(1);
      contentArea.appendChild(statusBadge);
    }

    // Details grid
    const detailsGrid = document.createElement('div');
    detailsGrid.className = 'pms-booking-mgmt-details';

    // Date & Time
    if (booking.startTime) {
      const dateRow = createDetailRow(
        'Date & Time',
        formatBookingDateTime(booking.startTime, booking.endTime, booking.timezone)
      );
      detailsGrid.appendChild(dateRow);
    }

    // Meeting Type
    if (booking.meetingType) {
      const typeRow = createDetailRow('Meeting Type', escapeHtml(booking.meetingType));
      detailsGrid.appendChild(typeRow);
    }

    // Location
    if (booking.location) {
      const locRow = createDetailRow('Location', escapeHtml(booking.location));
      detailsGrid.appendChild(locRow);
    }

    // Contact
    if (booking.contactName) {
      let contactText = escapeHtml(booking.contactName);
      if (booking.contactEmail) {
        contactText += `<br><span class="pms-booking-mgmt-detail-secondary">${escapeHtml(booking.contactEmail)}</span>`;
      }
      const contactRow = createDetailRow('Booked By', contactText);
      detailsGrid.appendChild(contactRow);
    }

    // Notes
    if (booking.notes) {
      const notesRow = createDetailRow('Notes', escapeHtml(booking.notes));
      detailsGrid.appendChild(notesRow);
    }

    contentArea.appendChild(detailsGrid);

    // Action buttons (only show if booking is not cancelled)
    if (booking.status !== 'cancelled' && onAction) {
      const actionsRow = document.createElement('div');
      actionsRow.className = 'pms-booking-mgmt-actions';

      const rescheduleBtn = document.createElement('button');
      rescheduleBtn.className = 'pms-booking-mgmt-btn pms-booking-mgmt-btn-reschedule';
      rescheduleBtn.textContent = 'Reschedule';
      rescheduleBtn.addEventListener('click', () => {
        onAction(`reschedule:${bmData.token}`);
      });
      actionsRow.appendChild(rescheduleBtn);

      const cancelBtn = document.createElement('button');
      cancelBtn.className = 'pms-booking-mgmt-btn pms-booking-mgmt-btn-cancel';
      cancelBtn.textContent = 'Cancel Booking';
      cancelBtn.addEventListener('click', () => {
        onAction(`cancel_booking:${bmData.token}`);
      });
      actionsRow.appendChild(cancelBtn);

      contentArea.appendChild(actionsRow);
    }
  }

  // =========================================================================
  // HELPERS
  // =========================================================================

  function createDetailRow(label: string, valueHtml: string): HTMLElement {
    const row = document.createElement('div');
    row.className = 'pms-booking-mgmt-detail-row';
    row.innerHTML = `
      <div class="pms-booking-mgmt-detail-label">${escapeHtml(label)}</div>
      <div class="pms-booking-mgmt-detail-value">${valueHtml}</div>
    `;
    return row;
  }

  function formatBookingDateTime(startIso: string, endIso?: string, tz?: string): string {
    try {
      const start = new Date(startIso);
      const datePart = start.toLocaleDateString([], {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
      });
      const startTime = start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });

      let result = `${escapeHtml(datePart)}<br>${escapeHtml(startTime)}`;

      if (endIso) {
        const end = new Date(endIso);
        const endTime = end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
        result += ` - ${escapeHtml(endTime)}`;
      }

      if (tz) {
        result += `<br><span class="pms-booking-mgmt-detail-secondary">${escapeHtml(tz.replace(/_/g, ' '))}</span>`;
      }

      return result;
    } catch {
      return escapeHtml(startIso);
    }
  }

  // Start fetch
  fetchBooking();
});
